import { CSSProperties, useEffect, useMemo, useState } from "react";
import { api } from "../api.js";
import { HorizontalBars, StatCard, VerticalBars } from "../components/Charts.js";
import { ClientDetail } from "./Clients.js";
import { MasterDetail } from "./Masters.js";
import { useIsModern } from "../theme.js";
import { ReportsTabModern } from "./Reports.modern.js";

interface TopClient {
  clientId: string;
  firstName: string;
  lastName: string | null;
  instagram: string | null;
  visits: number;
  spent: number;
  tips: number;
}

// Range is now a full PeriodValue (preset OR custom range). Old aliases kept
// so this file's heavy detail components stay readable.
import {
  PeriodPicker,
  periodLabel,
  periodToIsoRange,
  DATA_START_MONTH,
  monthTitle,
  currentMonthKey,
  type PeriodValue,
} from "../components/PeriodPicker.js";
type Range = PeriodValue;
type Source = "cash" | "bumpix";
const RANGE_LABEL = (r: Range) => periodLabel(r);

const SERVICE_LABEL: Record<string, string> = {
  manicure: "Манікюр",
  pedicure: "Педикюр",
  manicure_pedicure: "Манік + педик",
  other: "Інше",
};

const REFERRAL_REPORT_LABEL: Record<string, string> = {
  instagram: "📷 Instagram",
  google_maps: "🗺️ Google Maps",
  tiktok: "🎵 TikTok",
  public: "📢 Паблік",
  targeted_ads: "🎯 Таргет",
  recommendation: "🤝 Порекомендували",
  business_card: "💳 Візитка",
  cross_marketing: "🔁 Крос-маркетинг",
};

function rangeToDates(r: Range): { from: string; to: string } {
  return periodToIsoRange(r);
}

/** Choose a sensible bucket size for the given range. Short windows
 *  (≤ ~60 days) use weeks; everything else monthly. Custom ranges fall
 *  through the same heuristic — no special-casing needed. */
function bucketForRange(r: Range): "week" | "month" {
  const { from, to } = rangeToDates(r);
  const days =
    (new Date(to).getTime() - new Date(from).getTime()) / 86_400_000;
  return days <= 60 ? "week" : "month";
}

const UA_MONTHS_GEN = [
  "січ",
  "лют",
  "бер",
  "кві",
  "тра",
  "чер",
  "лип",
  "сер",
  "вер",
  "жов",
  "лис",
  "гру",
];

function formatBucketLabel(iso: string, bucket: "week" | "month"): string {
  const d = new Date(iso);
  if (bucket === "month") {
    return String(d.getMonth() + 1).padStart(2, "0");
  }
  // Week starts Monday (iso). Show "DD-DD" with the Sunday day-of-month.
  const monday = d;
  const sunday = new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000);
  return `${monday.getDate()}–${sunday.getDate()} ${UA_MONTHS_GEN[monday.getMonth()]}`;
}

// Точна сума до 1 грн з розділювачем тисяч ("16 057", не "16k") — люди
// плутались у заокруглених «k». Групування пробілами, знак зберігається.
function uahFmt(n: number) {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/** Full amount with space-grouped thousands ("12 400") — locale-free for iOS. */
function uahFull(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

const MASTER_SHARE = 0.4;
const STUDIO_SHARE = 0.6;

type AnalyticsView =
  | "money"
  | "expenses"
  | "trends"
  | "reviews"
  | "resources"
  | "shopping";

export function ReportsTab() {
  const modern = useIsModern();
  if (modern) return <ReportsTabModern />;
  return <LegacyReportsTab />;
}

function LegacyReportsTab() {
  const [subTab, setSubTab] = useState<AnalyticsView>("money");
  const [range, setRange] = useState<Range>({ preset: "1m" });
  const [source, setSource] = useState<Source>("cash");
  const [overview, setOverview] = useState<any | null>(null);
  // «Виручка по місяцях» — від першого місяця з даними до сьогодні,
  // незалежно від вибраного періоду. Те саме, що в модерн-дизайні.
  const [months, setMonths] = useState<
    { key: string; cash: number; bumpix: number; tips: number }[]
  >([]);
  useEffect(() => {
    const [y, m] = DATA_START_MONTH.split("-").map(Number);
    api.reports
      .timeline({
        from: new Date(y, m - 1, 1).toISOString(),
        to: new Date().toISOString(),
        bucket: "month",
      })
      .then((d: any) =>
        setMonths(
          (d?.items ?? [])
            .map((t: any) => ({
              key: String(t.bucket).slice(0, 7),
              cash: t.cashRevenue ?? 0,
              bumpix: t.bumpixRevenue ?? 0,
              tips: t.cashTips ?? 0,
            }))
            .filter((t: { key: string }) => t.key >= DATA_START_MONTH)
            .sort((a: { key: string }, b: { key: string }) => (a.key < b.key ? 1 : -1)),
        ),
      )
      .catch(() => setMonths([]));
  }, []);
  const [timeline, setTimeline] = useState<{
    bucket: "week" | "month";
    items: any[];
  } | null>(null);
  const [byMaster, setByMaster] = useState<any[] | null>(null);
  const [services, setServices] = useState<any[] | null>(null);
  const [payouts, setPayouts] = useState<any[] | null>(null);
  const [referrals, setReferrals] = useState<any[] | null>(null);
  const [topClients, setTopClients] = useState<TopClient[] | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [selectedMasterId, setSelectedMasterId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Trends data — loaded lazily when the "Тренди" sub-tab is opened.
  const [retention, setRetention] = useState<{
    newClients: number;
    returned: number;
    rate: number;
  } | null>(null);
  const [forecast, setForecast] = useState<{
    daysPassed: number;
    daysInMonth: number;
    mtd: { cash: number; bumpix: number };
    forecast: { cash: number; bumpix: number };
    prevMonth: { cash: number; bumpix: number };
  } | null>(null);
  const [heatmap, setHeatmap] = useState<
    { dow: number; hour: number; visits: number }[] | null
  >(null);
  const [masterRatings, setMasterRatings] = useState<
    { masterId: string; name: string; positive: number; negative: number }[]
    | null
  >(null);

  useEffect(() => {
    if (subTab !== "trends") return;
    const { from, to } = rangeToDates(range);
    setRetention(null);
    setHeatmap(null);
    setMasterRatings(null);
    Promise.all([
      api.reports.retention(from, to),
      api.reports.forecast(),
      api.reports.heatmap(from, to),
      api.reports.masterRatings(from, to),
    ])
      .then(([r, f, h, mr]) => {
        setRetention(r);
        setForecast(f);
        setHeatmap(h);
        setMasterRatings(mr);
      })
      .catch((e) => setError(String(e)));
  }, [range, subTab]);

  useEffect(() => {
    const { from, to } = rangeToDates(range);
    const bucket: "week" | "month" = bucketForRange(range);
    Promise.all([
      api.reports.overview(from, to),
      api.reports.timeline({ from, to, bucket }),
      api.reports.byMaster(from, to),
      api.reports.services(from, to),
      api.listPayouts(from, to),
      api.reports.referralSources(),
      api.reports.topClients(from, to),
    ])
      .then(([o, t, m, s, p, ref, tc]) => {
        setOverview(o);
        setTimeline(t);
        setByMaster(m);
        setServices(s);
        setPayouts(p);
        setReferrals(ref as any[]);
        setTopClients(tc as TopClient[]);
      })
      .catch((e) => setError(String(e)));
  }, [range]);

  const hasCashData = (overview?.cashEntries ?? 0) > 0;

  // If user is in cash mode but there's no cash yet, fall back visually to
  // Bumpix so the page isn't empty. The toggle stays as user set it.
  const effectiveSource: Source = source === "cash" && !hasCashData ? "bumpix" : source;

  // Compute view-model based on selected source.
  const view = useMemo(() => {
    if (!overview) return null;
    if (effectiveSource === "cash") {
      return {
        revenue: overview.cashRevenue,
        tips: overview.cashTips,
        avgCheck: overview.cashAvgCheck,
        studio: overview.studioRevenue,
        masters: overview.mastersPayout,
        sourceLabel: "Telegram",
        sourceHint: "точні суми зі звітів майстрів",
      };
    }
    return {
      revenue: overview.bumpixRevenue,
      tips: 0,
      avgCheck: overview.bumpixAvgCheck,
      studio: Math.round(overview.bumpixRevenue * STUDIO_SHARE),
      masters: Math.round(overview.bumpixRevenue * MASTER_SHARE),
      sourceLabel: "Bumpix",
      sourceHint: "орієнтовно з CRM (ціни не завжди точні)",
    };
  }, [overview, effectiveSource]);

  const monthlyBars = useMemo(() => {
    if (!timeline) return [];
    return timeline.items.map((m: any) => {
      const value = effectiveSource === "cash" ? m.cashRevenue : m.bumpixRevenue;
      const value2 = effectiveSource === "cash" ? m.cashTips : 0;
      return {
        label: formatBucketLabel(m.bucket, timeline.bucket),
        value,
        value2,
        display: uahFmt(value + value2),
      };
    });
  }, [timeline, effectiveSource]);

  const serviceBars = useMemo(
    () =>
      (services ?? []).map((s: any) => ({
        label: SERVICE_LABEL[s.service] ?? s.service,
        value: s.visits,
        display: `${s.visits} (${uahFmt(s.revenue)})`,
      })),
    [services],
  );

  const referralBars = useMemo(
    () =>
      (referrals ?? []).map((r: any) => ({
        label: REFERRAL_REPORT_LABEL[r.source] ?? r.source,
        value: r.count,
        display: String(r.count),
      })),
    [referrals],
  );

  if (error) {
    return (
      <div className="card">
        <p>Не вдалось завантажити звіти.</p>
        <p className="hint">{error}</p>
      </div>
    );
  }

  // Detail screens overlay; keep the dashboard mounted so its sub-tab,
  // range, scroll, and lazy-loaded sections survive a back navigation.
  const overlayOpen = !!selectedClientId || !!selectedMasterId;

  return (
    <>
      {selectedClientId && (
        <ClientDetail
          id={selectedClientId}
          onBack={() => setSelectedClientId(null)}
        />
      )}
      {!selectedClientId && selectedMasterId && (
        <MasterDetail
          id={selectedMasterId}
          onBack={() => setSelectedMasterId(null)}
        />
      )}
    <div style={{ display: overlayOpen ? "none" : "block" }}>
      {/* Sub-tabs of Аналітика. */}
      <div className="tabs" style={{ marginBottom: 8, flexWrap: "wrap" }}>
        <button
          className={subTab === "money" ? "active" : ""}
          onClick={() => setSubTab("money")}
        >
          💰 Гроші
        </button>
        <button
          className={subTab === "expenses" ? "active" : ""}
          onClick={() => setSubTab("expenses")}
        >
          💸 Витрати
        </button>
        <button
          className={subTab === "trends" ? "active" : ""}
          onClick={() => setSubTab("trends")}
        >
          📈 Тренди
        </button>
        <button
          className={subTab === "reviews" ? "active" : ""}
          onClick={() => setSubTab("reviews")}
        >
          ⭐ Відгуки
        </button>
        <button
          className={subTab === "resources" ? "active" : ""}
          onClick={() => setSubTab("resources")}
        >
          🪑 Ресурси
        </button>
        <button
          className={subTab === "shopping" ? "active" : ""}
          onClick={() => setSubTab("shopping")}
        >
          🛒 Закупки
        </button>
      </div>

      {subTab === "shopping" && <ShoppingView />}

      {subTab === "resources" && <ResourcesView />}

      {subTab === "expenses" && <ExpensesView range={range} setRange={setRange} />}

      {subTab === "trends" && (
        <TrendsView
          range={range}
          setRange={setRange}
          retention={retention}
          forecast={forecast}
          heatmap={heatmap}
          masterRatings={masterRatings}
          byMaster={byMaster}
        />
      )}

      {subTab === "reviews" && <ReviewsView range={range} setRange={setRange} />}

      {subTab === "money" && (
        <>
      <PeriodPicker value={range} onChange={setRange} />

      {/* Source selector */}
      <div className="tabs" style={{ marginBottom: 12 }}>
        <button
          className={source === "cash" ? "active" : ""}
          onClick={() => setSource("cash")}
          title="Точні суми зі звітів майстрів"
        >
          💰 Telegram
        </button>
        <button
          className={source === "bumpix" ? "active" : ""}
          onClick={() => setSource("bumpix")}
          title="Орієнтовні ціни з Bumpix"
        >
          📊 Bumpix
        </button>
      </div>

      {!overview || !view ? (
        <div className="hint">Завантаження...</div>
      ) : (
        <>
          {source === "cash" && !hasCashData && (
            <div className="card" style={{ background: "rgba(255,193,7,0.15)" }}>
              <p style={{ margin: 0 }}>
                💡 Поки нема жодного звіту від майстрів у Telegram.
              </p>
              <p className="hint" style={{ marginTop: 4 }}>
                Показую дані з Bumpix. Як майстри почнуть слати звіти боту —
                сюди підставляться реальні суми.
              </p>
              <pre style={preStyle}>{"16.05\nМанікюр 1000\nПедикюр 1200, 100 чай\nГель-лак 900 (-20% промокод)\nРемонт (гарантія)\nМанікюр 1000 (блогер)\nЧайові 200"}</pre>
            </div>
          )}

          <div className="hint" style={{ marginBottom: 8 }}>
            Джерело: <b>{view.sourceLabel}</b> · {view.sourceHint}
          </div>

          <OrphanedCashBanner
            range={range}
            byMaster={byMaster}
            onChanged={() => {
              const { from, to } = rangeToDates(range);
              const bucket: "week" | "month" = bucketForRange(range);
              Promise.all([
                api.reports.overview(from, to),
                api.reports.timeline({ from, to, bucket }),
                api.reports.byMaster(from, to),
              ]).then(([o, t, m]) => {
                setOverview(o);
                setTimeline(t);
                setByMaster(m);
              });
            }}
          />

          {/* By master — FIRST */}
          <div className="card">
            <h2 style={{ marginTop: 0 }}>👩 По майстрах · {RANGE_LABEL(range)}</h2>
            {byMaster && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {byMaster
                  .filter((m: any) =>
                    effectiveSource === "cash" ? m.cashRevenue > 0 : m.bumpixRevenue > 0,
                  )
                  .map((m: any) => (
                    <MasterRow
                      key={m.masterId}
                      m={m}
                      source={effectiveSource}
                      range={range}
                      onOpen={() => setSelectedMasterId(m.masterId)}
                      onPaid={() => {
                        const { from, to } = rangeToDates(range);
                        const bucket: "week" | "month" = bucketForRange(range);
                        Promise.all([
                          api.reports.overview(from, to),
                          api.reports.timeline({ from, to, bucket }),
                          api.reports.byMaster(from, to),
                        ]).then(([o, t, m]) => {
                          setOverview(o);
                          setTimeline(t);
                          setByMaster(m);
                        });
                      }}
                    />
                  ))}
              </div>
            )}
          </div>

          {/* General KPIs */}
          <h2 style={{ margin: "8px 0 4px", fontSize: 16 }}>Загальне · {RANGE_LABEL(range)}</h2>
          <div style={gridStyle}>
            <StatCard
              label="Виручка"
              value={uahFmt(view.revenue) + " грн"}
              hint={view.tips > 0 ? `+ ${uahFmt(view.tips)} грн чайові` : "без чайових"}
              color="#10b981"
            />
            <StatCard
              label="Середній чек"
              value={uahFmt(view.avgCheck) + " грн"}
            />
            <StatCard
              label="Дохід студії"
              value={uahFmt(view.studio) + " грн"}
              hint="60% з послуг"
              color="#7c3aed"
            />
            <StatCard
              label="Виплати майстрам"
              value={uahFmt(view.masters) + " грн"}
              hint={view.tips > 0 ? "40% + 100% чайових" : "40% з послуг"}
              color="#10b981"
            />
          </div>

          {/* Activity from Bumpix */}
          <div style={gridStyle}>
            <StatCard label="Візити" value={overview.visits} hint="з Bumpix" />
            <StatCard label="Клієнти" value={overview.uniqueClients} hint="унікальних" />
            <StatCard label="Нові" value={overview.newClients} hint="перший візит" />
          </div>

          {months.length > 0 && (
            <div className="card">
              <h2 style={{ marginTop: 0 }}>🗓 Виручка по місяцях</h2>
              <p className="hint" style={{ marginBottom: 8 }}>
                Тицьни місяць — і весь екран покаже саме його.
              </p>
              {months.map((mo) => {
                const isCurrent = mo.key === currentMonthKey();
                const selected = isCurrent
                  ? range.preset === "1m"
                  : range.preset === "month" && range.month === mo.key;
                const v = effectiveSource === "cash" ? mo.cash : mo.bumpix;
                return (
                  <button
                    key={mo.key}
                    onClick={() =>
                      setRange(isCurrent ? { preset: "1m" } : { preset: "month", month: mo.key })
                    }
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      padding: "8px 10px",
                      border: 0,
                      borderTop: "1px solid rgba(127,127,127,0.15)",
                      borderRadius: 8,
                      background: selected ? "var(--secondary-bg)" : "transparent",
                      color: "var(--text)",
                      fontSize: 14,
                      fontWeight: selected ? 700 : 500,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <span>
                      {monthTitle(mo.key)}
                    </span>
                    <span style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      {uahFull(v)} грн
                      {effectiveSource === "cash" && mo.tips > 0 && (
                        <span className="hint" style={{ fontWeight: 500 }}> + {uahFull(mo.tips)} чай</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Timeline chart */}
          <div className="card">
            <h2 style={{ marginTop: 0 }}>
              📈 Виручка по {timeline?.bucket === "week" ? "тижнях" : "місяцях"} (
              {timeline?.items.length ?? 0})
            </h2>
            <p className="hint" style={{ marginBottom: 8 }}>
              Джерело: {view.sourceLabel}
              {effectiveSource === "cash" ? " — фіолетовий послуги, світліший чайові" : ""}
            </p>
            {timeline ? (
              <VerticalBars
                data={monthlyBars}
                color="#7c3aed"
                color2="#c4b5fd"
                height={220}
              />
            ) : null}
          </div>

          {/* Top clients by money */}
          <TopClientsCard
            items={topClients}
            range={range}
            onOpen={setSelectedClientId}
          />

          {/* Services */}
          <div className="card">
            <h2 style={{ marginTop: 0 }}>💅 Послуги · {RANGE_LABEL(range)}</h2>
            <p className="hint" style={{ marginBottom: 8 }}>
              Кількість візитів з Bumpix
            </p>
            {services ? <HorizontalBars data={serviceBars} /> : null}
          </div>

          {/* Referral sources */}
          {referrals && referrals.length > 0 && (
            <div className="card">
              <h2 style={{ marginTop: 0 }}>📍 Звідки приходять клієнти</h2>
              <p className="hint" style={{ marginBottom: 8 }}>
                З відповідей на «звідки про нас дізналися» (за весь час)
              </p>
              <HorizontalBars data={referralBars} />
            </div>
          )}

          {/* Payout history */}
          {payouts && payouts.length > 0 && (
            <div className="card">
              <h2 style={{ marginTop: 0 }}>📒 Виплати · {RANGE_LABEL(range)}</h2>
              <p className="hint" style={{ marginBottom: 8 }}>
                Всього виплачено: {uahFmt(payouts.reduce((s, p) => s + p.amount, 0))} грн
              </p>
              {payouts.map((p) => (
                <div key={p.id} style={payoutLine}>
                  <div className="row">
                    <span style={{ fontWeight: 600, minWidth: 80 }}>
                      {(() => {
                        const d = new Date(p.paidAt);
                        return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
                      })()}
                    </span>
                    <span style={{ fontWeight: 600, flex: 1 }}>{p.masterName}</span>
                    <span style={{ fontWeight: 700, color: "#10b981" }}>
                      {uahFmt(p.amount)} грн
                    </span>
                  </div>
                  <div className="hint" style={{ fontSize: 11, marginTop: 2 }}>
                    40%: {uahFmt(p.fromPrices)} · чай: {uahFmt(p.fromTips)} · {p.cashEntryCount} записів
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
        </>
      )}
    </div>
    </>
  );
}

// ---- Trends sub-tab ----

const DAY_LABELS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"];

function TrendsView({
  range,
  setRange,
  retention,
  forecast,
  heatmap,
  masterRatings,
  byMaster,
}: {
  range: Range;
  setRange: (r: Range) => void;
  retention: { newClients: number; returned: number; rate: number } | null;
  forecast: {
    daysPassed: number;
    daysInMonth: number;
    mtd: { cash: number; bumpix: number };
    forecast: { cash: number; bumpix: number };
    prevMonth: { cash: number; bumpix: number };
  } | null;
  heatmap: { dow: number; hour: number; visits: number }[] | null;
  masterRatings:
    | { masterId: string; name: string; positive: number; negative: number }[]
    | null;
  byMaster: any[] | null;
}) {
  // Choose CASH if there's any cash data; otherwise fall back to Bumpix.
  const useCash =
    !!forecast && (forecast.mtd.cash > 0 || forecast.prevMonth.cash > 0);
  const fc = forecast?.forecast[useCash ? "cash" : "bumpix"] ?? 0;
  const mtd = forecast?.mtd[useCash ? "cash" : "bumpix"] ?? 0;
  const prev = forecast?.prevMonth[useCash ? "cash" : "bumpix"] ?? 0;

  let trendArrow = "";
  let trendPct = 0;
  let trendColor: string = "var(--text)";
  if (forecast && prev > 0) {
    const diff = fc - prev;
    trendPct = Math.round((diff / prev) * 100);
    trendArrow = diff > 0 ? "↑" : diff < 0 ? "↓" : "→";
    trendColor = diff > 0 ? "#10b981" : diff < 0 ? "#e74c3c" : "var(--text)";
  }

  const tipLeaders = (byMaster ?? [])
    .filter((m: any) => m.cashTips > 0)
    .sort((a: any, b: any) => b.cashTips - a.cashTips);

  return (
    <div>
      <PeriodPicker value={range} onChange={setRange} />

      {forecast && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>🔮 Прогноз на цей місяць</h2>
          <div
            style={{
              fontSize: 28,
              fontWeight: 700,
              color: trendColor,
              marginBottom: 4,
            }}
          >
            {uahFmt(fc)} грн {trendArrow}
            {trendPct !== 0 && (
              <span style={{ fontSize: 16, marginLeft: 6 }}>
                {trendPct > 0 ? "+" : ""}
                {trendPct}%
              </span>
            )}
          </div>
          <div className="hint" style={{ fontSize: 12, lineHeight: 1.5 }}>
            Минулого місяця: <b>{uahFmt(prev)} грн</b>
            <br />
            Поки набрали: <b>{uahFmt(mtd)} грн</b> за {forecast.daysPassed} днів
            з {forecast.daysInMonth}
            <br />
            <span style={{ opacity: 0.7 }}>
              Джерело: {useCash ? "Telegram-звіти майстрів" : "Bumpix (касових ще нема)"}
            </span>
          </div>
        </div>
      )}

      {retention && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>📈 Retention · {RANGE_LABEL(range)}</h2>
          <div
            style={{
              fontSize: 28,
              fontWeight: 700,
              color:
                retention.rate >= 50
                  ? "#10b981"
                  : retention.rate >= 25
                    ? "#f59e0b"
                    : "#e74c3c",
            }}
          >
            {retention.rate}%
          </div>
          <div className="hint" style={{ fontSize: 12 }}>
            <b>{retention.returned}</b> з <b>{retention.newClients}</b> новеньких
            клієнток повернулись на 2-й візит.
          </div>
        </div>
      )}

      {masterRatings && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>
            ⭐ Рейтинг майстрів · {RANGE_LABEL(range)}
          </h2>
          {masterRatings.length === 0 ? (
            <p className="hint" style={{ margin: 0 }}>
              За цей період відгуків ще нема.
            </p>
          ) : (
            masterRatings.map((m) => {
              const total = m.positive + m.negative;
              const ratio = total > 0 ? Math.round((m.positive / total) * 100) : 0;
              return (
                <div key={m.masterId} className="row" style={ratingRow}>
                  <span style={{ fontWeight: 600 }}>{m.name}</span>
                  <span className="spacer" />
                  <span style={{ marginRight: 8, fontSize: 12 }}>
                    👍 {m.positive} · 👎 {m.negative}
                  </span>
                  <span
                    style={{
                      fontWeight: 700,
                      color:
                        ratio >= 80
                          ? "#10b981"
                          : ratio >= 50
                            ? "#f59e0b"
                            : "#e74c3c",
                    }}
                  >
                    {ratio}%
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}

      {tipLeaders.length > 0 && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>
            💸 Лідерборд чайових · {RANGE_LABEL(range)}
          </h2>
          {tipLeaders.map((m: any, i: number) => {
            const medal =
              i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`;
            return (
              <div key={m.masterId} className="row" style={ratingRow}>
                <span style={{ minWidth: 30, fontWeight: 700 }}>{medal}</span>
                <span style={{ fontWeight: 600, flex: 1 }}>{m.name}</span>
                <span style={{ fontWeight: 700, color: "#f59e0b" }}>
                  {uahFmt(m.cashTips)} грн
                </span>
              </div>
            );
          })}
        </div>
      )}

      {heatmap && heatmap.length > 0 && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>
            🔥 Завантаженість · {RANGE_LABEL(range)}
          </h2>
          <HeatmapSummary data={heatmap} />
          <HeatmapGrid data={heatmap} />
        </div>
      )}

      {(!retention || !forecast || !heatmap || !masterRatings) && (
        <div className="hint">Завантаження...</div>
      )}
    </div>
  );
}

function HeatmapSummary({
  data,
}: {
  data: { dow: number; hour: number; visits: number }[];
}) {
  const byDow: Record<number, number> = {};
  const byHour: Record<number, number> = {};
  for (const r of data) {
    byDow[r.dow] = (byDow[r.dow] ?? 0) + r.visits;
    byHour[r.hour] = (byHour[r.hour] ?? 0) + r.visits;
  }
  const topDow = Object.entries(byDow).sort((a, b) => b[1] - a[1])[0];
  const topHour = Object.entries(byHour).sort((a, b) => b[1] - a[1])[0];
  return (
    <p className="hint" style={{ marginTop: 0, marginBottom: 8, fontSize: 13 }}>
      Пікові: день — <b>{topDow ? DAY_LABELS[Number(topDow[0]) - 1] : "—"}</b>,
      година — <b>{topHour ? `${topHour[0]}:00` : "—"}</b>.
    </p>
  );
}

function HeatmapGrid({
  data,
}: {
  data: { dow: number; hour: number; visits: number }[];
}) {
  const matrix: Record<number, Record<number, number>> = {};
  let max = 0;
  for (const r of data) {
    matrix[r.hour] ??= {};
    matrix[r.hour]![r.dow] = r.visits;
    if (r.visits > max) max = r.visits;
  }
  const hoursWithData = Object.keys(matrix)
    .map(Number)
    .sort((a, b) => a - b);
  const days = [1, 2, 3, 4, 5, 6, 7];

  if (hoursWithData.length === 0) return null;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "36px repeat(7, 1fr)",
        gap: 2,
        fontSize: 11,
      }}
    >
      <div />
      {DAY_LABELS.map((d, i) => (
        <div
          key={`d-${i}`}
          style={{ textAlign: "center", fontWeight: 600, padding: 2 }}
        >
          {d}
        </div>
      ))}
      {hoursWithData.flatMap((h) => [
        <div
          key={`hr-${h}`}
          style={{
            textAlign: "right",
            paddingRight: 4,
            color: "var(--hint)",
            lineHeight: "22px",
          }}
        >
          {h}:00
        </div>,
        ...days.map((d) => {
          const v = matrix[h]?.[d] ?? 0;
          const t = max > 0 ? v / max : 0;
          return (
            <div
              key={`c-${h}-${d}`}
              style={{
                padding: 2,
                textAlign: "center",
                borderRadius: 3,
                background:
                  t === 0
                    ? "rgba(127,127,127,0.08)"
                    : `rgba(124, 58, 237, ${0.15 + t * 0.75})`,
                color: t > 0.55 ? "white" : "var(--text)",
                fontWeight: t > 0.5 ? 600 : 400,
                minHeight: 22,
                lineHeight: "22px",
              }}
            >
              {v || ""}
            </div>
          );
        }),
      ])}
    </div>
  );
}

const ratingRow: CSSProperties = {
  padding: "6px 0",
  borderBottom: "1px solid rgba(127,127,127,0.15)",
  fontSize: 13,
};

// ---- Reviews sub-tab ----

const SENTIMENT_LABEL: Record<string, { icon: string; label: string; color: string }> = {
  positive: { icon: "👍", label: "Позитив", color: "#10b981" },
  negative: { icon: "👎", label: "Скарга", color: "#e74c3c" },
  mixed: { icon: "🟡", label: "50/50", color: "#f59e0b" },
};

type SentimentFilter = "all" | "positive" | "negative" | "mixed";

function ReviewsView({
  range,
  setRange,
}: {
  range: Range;
  setRange: (r: Range) => void;
}) {
  const [data, setData] = useState<{
    counts: { positive: number; negative: number; mixed: number };
    googleFunnel: { asked: number; left: number; rate: number };
    items: {
      id: string;
      actedAt: string;
      sentiment: "positive" | "negative" | "mixed";
      note: string | null;
      client: {
        id: string;
        firstName: string;
        lastName: string | null;
        instagram: string | null;
        googleReviewLeft: boolean;
      };
      masterName: string | null;
    }[];
  } | null>(null);
  const [filter, setFilter] = useState<SentimentFilter>("all");
  const [error, setError] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

  function load() {
    const { from, to } = rangeToDates(range);
    setData(null);
    api.reviews(from, to).then(setData).catch((e) => setError(String(e)));
  }
  useEffect(load, [range]);

  if (error)
    return (
      <div className="card">
        <p>Помилка: {error}</p>
      </div>
    );
  if (!data) return <div className="hint">Завантаження...</div>;

  const filtered =
    filter === "all"
      ? data.items
      : data.items.filter((i) => i.sentiment === filter);

  return (
    <>
      {selectedClientId && (
        <ClientDetail
          id={selectedClientId}
          onBack={() => {
            setSelectedClientId(null);
            load();
          }}
        />
      )}
    <div style={{ display: selectedClientId ? "none" : "block" }}>
      <PeriodPicker value={range} onChange={setRange} />

      {/* Counts overview */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <StatCard
          label="👍 Позитив"
          value={data.counts.positive}
          color="#10b981"
        />
        <StatCard
          label="🟡 50/50"
          value={data.counts.mixed}
          color="#f59e0b"
        />
        <StatCard
          label="👎 Скарги"
          value={data.counts.negative}
          color="#e74c3c"
        />
      </div>

      {/* Google funnel */}
      <div className="card">
        <h2 style={{ marginTop: 0 }}>🌟 Google-воронка</h2>
        <div className="row" style={profitRow}>
          <span className="hint">Попросили лишити відгук (👍)</span>
          <span className="spacer" />
          <span style={{ fontWeight: 600 }}>{data.googleFunnel.asked}</span>
        </div>
        <div className="row" style={profitRow}>
          <span className="hint">Реально лишили</span>
          <span className="spacer" />
          <span style={{ fontWeight: 600, color: "#10b981" }}>
            {data.googleFunnel.left}
          </span>
        </div>
        <div
          className="row"
          style={{
            ...profitRow,
            borderBottom: 0,
            borderTop: "1px solid rgba(127,127,127,0.25)",
            marginTop: 6,
            paddingTop: 8,
          }}
        >
          <span style={{ fontWeight: 700 }}>Конверсія</span>
          <span className="spacer" />
          <span
            style={{
              fontSize: 22,
              fontWeight: 700,
              color:
                data.googleFunnel.rate >= 50
                  ? "#10b981"
                  : data.googleFunnel.rate >= 20
                    ? "#f59e0b"
                    : "#e74c3c",
            }}
          >
            {data.googleFunnel.rate}%
          </span>
        </div>
        <p className="hint" style={{ fontSize: 11, marginTop: 8, marginBottom: 0 }}>
          «Лишили» рахується по галочці у профілі клієнтки — постав її, коли побачиш реальний відгук на Google.
        </p>
      </div>

      {/* Filter */}
      <div className="tabs" style={{ marginBottom: 8 }}>
        <button
          className={filter === "all" ? "active" : ""}
          onClick={() => setFilter("all")}
        >
          Усі
        </button>
        <button
          className={filter === "positive" ? "active" : ""}
          onClick={() => setFilter("positive")}
        >
          👍
        </button>
        <button
          className={filter === "mixed" ? "active" : ""}
          onClick={() => setFilter("mixed")}
        >
          🟡
        </button>
        <button
          className={filter === "negative" ? "active" : ""}
          onClick={() => setFilter("negative")}
        >
          👎
        </button>
      </div>

      {/* Feed */}
      {filtered.length === 0 ? (
        <div className="card">
          <p style={{ margin: 0 }} className="hint">
            За цей період відгуків нема.
          </p>
        </div>
      ) : (
        filtered.map((r) => {
          const s = SENTIMENT_LABEL[r.sentiment]!;
          const name =
            `${r.client.firstName} ${r.client.lastName ?? ""}`.trim();
          const d = new Date(r.actedAt);
          const dateStr = `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
          return (
            <div
              key={r.id}
              className="card"
              style={{ padding: 10, marginBottom: 6 }}
            >
              <div
                onClick={() => setSelectedClientId(r.client.id)}
                style={{ cursor: "pointer" }}
              >
                <div className="row">
                  <span style={{ fontSize: 18, marginRight: 6 }}>{s.icon}</span>
                  <span style={{ fontWeight: 700, color: s.color }}>{s.label}</span>
                  <span className="spacer" />
                  <span className="hint" style={{ fontSize: 12 }}>{dateStr}</span>
                </div>
                <div style={{ fontWeight: 600, marginTop: 4 }}>
                  {name}
                  {r.client.googleReviewLeft && (
                    <span
                      style={{
                        marginLeft: 6,
                        fontSize: 11,
                        padding: "2px 6px",
                        background: "rgba(16,185,129,0.18)",
                        color: "#059669",
                        borderRadius: 8,
                        fontWeight: 600,
                      }}
                    >
                      🌟 Лишила Google-відгук
                    </span>
                  )}
                </div>
                {r.masterName && (
                  <div className="hint" style={{ fontSize: 12, marginTop: 2 }}>
                    👩 {r.masterName}
                  </div>
                )}
                {r.note && (
                  <div
                    style={{
                      marginTop: 6,
                      padding: 8,
                      background: "var(--bg)",
                      border: "1px solid rgba(127,127,127,0.2)",
                      borderRadius: 6,
                      fontSize: 13,
                      fontStyle: "italic",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    «{r.note}»
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
    </>
  );
}

// ---- Expenses sub-tab ----

const CATEGORY_ICONS: Record<string, string> = {
  Оренда: "🏠",
  Комунальні: "💡",
  Матеріали: "💅",
  "Сервіс (вода/кава/солодке)": "☕",
  Обладнання: "🛠️",
  "Інтернет + телефон": "📱",
  "ЗП адміну": "👤",
  CRM: "💻",
  Корпоративи: "🥂",
  Уніформа: "👕",
  "Подарунки (клієнтам / майстрам)": "🎁",
  Реклама: "📢",
  Поліграфія: "🖨️",
  Блогери: "📣",
  Ремонт: "🔧",
  Доставка: "📦",
  "Компенсація клієнтам": "💸",
};

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ---- Resource limits + booking-conflict alerts ----

type ResourceLimit = {
  id: string;
  label: string;
  pattern: string;
  capacity: number;
  isActive: boolean;
};

type ConflictAlert = {
  id: string;
  windowStart: string;
  windowEnd: string;
  notifiedAt: string | null;
  dismissedAt: string | null;
  resourceLimit: { id: string; label: string; capacity: number };
  visits: {
    id: string;
    startedAt: string;
    serviceLabel: string | null;
    client: { id: string; name: string } | null;
    master: { id: string; name: string } | null;
  }[];
};

function fmtConflictTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("uk-UA", {
    timeZone: "Europe/Kyiv",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ---- Shopping (masters ask Lyuba to buy stuff) ----

interface ShoppingRow {
  id: string;
  text: string;
  status: "pending" | "done" | "cancelled";
  createdAt: string;
  actedAt: string | null;
  master: { id: string; name: string; telegramId: string | null };
}

function ShoppingView() {
  const [pending, setPending] = useState<ShoppingRow[] | null>(null);
  const [history, setHistory] = useState<ShoppingRow[] | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyShown, setHistoryShown] = useState(10);
  const [error, setError] = useState<string | null>(null);

  function loadPending() {
    api
      .shoppingList("pending")
      .then((d) => setPending(d as ShoppingRow[]))
      .catch((e) => setError(String(e)));
  }
  function loadHistory() {
    api
      .shoppingList("done,cancelled")
      .then((d) => setHistory(d as ShoppingRow[]))
      .catch(() => setHistory([]));
  }
  useEffect(() => {
    loadPending();
  }, []);

  async function markDone(id: string) {
    try {
      await api.shoppingDone(id);
      loadPending();
      if (history) loadHistory();
    } catch (e) {
      alert(String(e));
    }
  }
  async function cancel(id: string) {
    if (!window.confirm("Відмінити цей запит?")) return;
    try {
      await api.shoppingCancel(id);
      loadPending();
      if (history) loadHistory();
    } catch (e) {
      alert(String(e));
    }
  }
  async function reopen(id: string) {
    try {
      await api.shoppingReopen(id);
      loadPending();
      loadHistory();
    } catch (e) {
      alert(String(e));
    }
  }

  if (error)
    return (
      <div className="card">
        <p>Помилка: {error}</p>
      </div>
    );
  if (!pending) return <div className="hint">Завантаження...</div>;

  return (
    <div>
      <p className="hint" style={{ marginBottom: 8 }}>
        Майстри пишуть боту <code>/buy текст</code> або
        «<i>купити: гель-лак, фольга</i>». Тут — їхній список запитів.
      </p>

      {pending.length === 0 ? (
        <div className="card">
          <p style={{ margin: 0 }}>Закупок нема 🎉</p>
        </div>
      ) : (
        pending.map((it) => (
          <div
            key={it.id}
            className="card"
            style={{ borderLeft: "4px solid #ec4899", padding: 12 }}
          >
            <div className="row" style={{ marginBottom: 4 }}>
              <span style={{ fontWeight: 700 }}>👤 {it.master.name}</span>
              <span style={{ flex: 1 }} />
              <span className="hint" style={{ fontSize: 12 }}>
                {fmtShortDateTime(it.createdAt)}
              </span>
            </div>
            <div
              style={{
                whiteSpace: "pre-wrap",
                fontSize: 14,
                lineHeight: 1.4,
                marginBottom: 10,
              }}
            >
              {it.text}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={() => markDone(it.id)}
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  background: "#10b981",
                  color: "#fff",
                  border: 0,
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                ✅ Куплено
              </button>
              <button
                onClick={() => cancel(it.id)}
                style={{
                  padding: "8px 12px",
                  background: "transparent",
                  color: "#dc2626",
                  border: "1px solid rgba(220,38,38,0.4)",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                ✕ Відмінити
              </button>
            </div>
          </div>
        ))
      )}

      <button
        onClick={() => {
          const next = !showHistory;
          setShowHistory(next);
          if (next && !history) loadHistory();
        }}
        style={{
          width: "100%",
          padding: "10px 14px",
          marginTop: 12,
          borderRadius: 10,
          border: "1px dashed rgba(127,127,127,0.4)",
          background: "transparent",
          color: "var(--text)",
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        {showHistory ? "▴ Сховати історію" : "▾ Показати історію (куплені / відмінені)"}
      </button>

      {showHistory && history && (
        <div style={{ marginTop: 10 }}>
          {history.length === 0 ? (
            <p className="hint">Поки порожньо.</p>
          ) : (
            <>
              {history.slice(0, historyShown).map((it) => (
                <div
                  key={it.id}
                  className="card"
                  style={{
                    padding: 10,
                    marginBottom: 6,
                    opacity: 0.85,
                    borderLeft: `4px solid ${it.status === "done" ? "#10b981" : "#9ca3af"}`,
                  }}
                >
                  <div className="row">
                    <span style={{ fontWeight: 600 }}>👤 {it.master.name}</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ fontSize: 11, fontWeight: 700, color: it.status === "done" ? "#047857" : "#6b7280" }}>
                      {it.status === "done" ? "✅ Куплено" : "✕ Відмінено"}
                    </span>
                  </div>
                  <div
                    style={{
                      whiteSpace: "pre-wrap",
                      fontSize: 13,
                      lineHeight: 1.4,
                      margin: "4px 0",
                    }}
                  >
                    {it.text}
                  </div>
                  <div className="hint" style={{ fontSize: 11 }}>
                    {fmtShortDateTime(it.createdAt)}
                    {it.actedAt ? ` → ${fmtShortDateTime(it.actedAt)}` : ""}
                    <span style={{ float: "right" }}>
                      <button
                        onClick={() => reopen(it.id)}
                        style={{
                          background: "transparent",
                          border: 0,
                          color: "var(--link)",
                          fontSize: 11,
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        повернути в активні
                      </button>
                    </span>
                  </div>
                </div>
              ))}
              {historyShown < history.length && (
                <button
                  onClick={() => setHistoryShown(historyShown + 10)}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: "1px dashed rgba(127,127,127,0.4)",
                    background: "transparent",
                    color: "var(--text)",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  ⬇ Показати ще (+{Math.min(10, history.length - historyShown)})
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function fmtShortDateTime(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}.${mm} · ${hh}:${mi}`;
}

function ResourcesView() {
  const [limits, setLimits] = useState<ResourceLimit[] | null>(null);
  const [future, setFuture] = useState<ConflictAlert[] | null>(null);
  const [past, setPast] = useState<ConflictAlert[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [scope, setScope] = useState<"future" | "past">("future");

  function loadLimits() {
    api
      .resourceLimits()
      .then((r) => setLimits(r as ResourceLimit[]))
      .catch((e) => setError(String(e)));
  }
  function loadConflicts() {
    api
      .conflicts("future")
      .then((r) => setFuture(r as ConflictAlert[]))
      .catch((e) => setError(String(e)));
    api
      .conflicts("past")
      .then((r) => setPast(r as ConflictAlert[]))
      .catch((e) => setError(String(e)));
  }
  useEffect(() => {
    loadLimits();
    loadConflicts();
  }, []);

  const [scanResult, setScanResult] = useState<string | null>(null);
  async function runScan() {
    setScanBusy(true);
    setScanResult(null);
    try {
      const r = (await api.scanConflicts()) as {
        newClusters: number;
        pushed: number;
        resolved?: number;
        syncMs?: number;
        syncError?: string | null;
      };
      loadConflicts();
      const parts: string[] = [];
      if (r.syncError) {
        parts.push(`Sync ⚠️ ${r.syncError.slice(0, 80)}`);
      } else if (r.syncMs !== undefined) {
        parts.push(`Bumpix sync: ${(r.syncMs / 1000).toFixed(0)}с`);
      }
      if (r.newClusters > 0) {
        parts.push(`Знайдено ${r.newClusters} нових`);
      }
      if (r.resolved && r.resolved > 0) {
        parts.push(`Авто-закрито: ${r.resolved}`);
      }
      if (r.newClusters === 0 && (!r.resolved || r.resolved === 0)) {
        parts.push("Все чисто 🎉");
      }
      setScanResult(parts.join(" · "));
      setTimeout(() => setScanResult(null), 8000);
    } catch (e) {
      setError(String(e));
    } finally {
      setScanBusy(false);
    }
  }

  async function dismiss(id: string) {
    try {
      await api.dismissConflict(id);
      loadConflicts();
    } catch (e) {
      setError(String(e));
    }
  }

  const visible = scope === "future" ? future : past;

  return (
    <div>
      {error && (
        <div className="card">
          <p className="hint">{error}</p>
        </div>
      )}

      <ConflictsBlock
        scope={scope}
        setScope={setScope}
        conflicts={visible}
        futureCount={future?.length ?? 0}
        pastCount={past?.length ?? 0}
        onDismiss={dismiss}
        onRescan={runScan}
        scanBusy={scanBusy}
        scanResult={scanResult}
      />

      <ResourceLimitsBlock
        limits={limits}
        onChanged={() => {
          loadLimits();
          loadConflicts();
        }}
      />
    </div>
  );
}

function ConflictsBlock({
  scope,
  setScope,
  conflicts,
  futureCount,
  pastCount,
  onDismiss,
  onRescan,
  scanBusy,
  scanResult,
}: {
  scope: "future" | "past";
  setScope: (s: "future" | "past") => void;
  conflicts: ConflictAlert[] | null;
  futureCount: number;
  pastCount: number;
  onDismiss: (id: string) => void;
  onRescan: () => void;
  scanBusy: boolean;
  scanResult: string | null;
}) {
  return (
    <div className="card">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <b>⚠️ Конфлікти розкладу</b>
        <button
          className="btn-secondary"
          onClick={onRescan}
          disabled={scanBusy}
          style={{ fontSize: 12, padding: "4px 10px" }}
        >
          {scanBusy ? "⏳ Синхронізую..." : "🔄 Просканувати"}
        </button>
      </div>
      {scanResult && (
        <div
          style={{
            background: "rgba(16,185,129,0.12)",
            border: "1px solid rgba(16,185,129,0.3)",
            color: "#047857",
            padding: "6px 10px",
            borderRadius: 8,
            fontSize: 13,
            marginBottom: 10,
          }}
        >
          {scanResult}
        </div>
      )}

      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        <button
          className={scope === "future" ? "btn" : "btn-secondary"}
          onClick={() => setScope("future")}
          style={{ fontSize: 13, padding: "4px 12px" }}
        >
          🔮 Майбутні · {futureCount}
        </button>
        <button
          className={scope === "past" ? "btn" : "btn-secondary"}
          onClick={() => setScope("past")}
          style={{ fontSize: 13, padding: "4px 12px" }}
        >
          📜 Минулі (30 дн) · {pastCount}
        </button>
      </div>

      {conflicts === null ? (
        <div className="hint">Завантаження...</div>
      ) : conflicts.length === 0 ? (
        <p className="hint" style={{ margin: 0 }}>
          {scope === "future"
            ? "Все чисто. Жодних накладок попереду 🎉"
            : "За останні 30 днів накладок не було."}
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {conflicts.map((c) => (
            <ConflictCard
              key={c.id}
              c={c}
              onDismiss={scope === "future" ? () => onDismiss(c.id) : null}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ConflictCard({
  c,
  onDismiss,
}: {
  c: ConflictAlert;
  onDismiss: (() => void) | null;
}) {
  return (
    <div
      style={{
        border: "1px solid var(--tg-theme-hint-color, #ccc)",
        borderRadius: 8,
        padding: 10,
        background: "var(--tg-theme-secondary-bg-color, #f4f4f5)",
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
        {c.resourceLimit.label} · {c.visits.length}/{c.resourceLimit.capacity}{" "}
        одночасно
      </div>
      <div className="hint" style={{ fontSize: 12, marginBottom: 6 }}>
        {fmtConflictTime(c.windowStart)} – {fmtConflictTime(c.windowEnd)}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {c.visits.map((v) => {
          const t = new Date(v.startedAt).toLocaleTimeString("uk-UA", {
            timeZone: "Europe/Kyiv",
            hour: "2-digit",
            minute: "2-digit",
          });
          return (
            <div key={v.id} style={{ fontSize: 13 }}>
              🕐 {t} — <b>{v.client?.name ?? "—"}</b> →{" "}
              {v.master?.name ?? "—"}
              {v.serviceLabel ? (
                <span className="hint" style={{ fontSize: 11 }}>
                  {" "}
                  · {v.serviceLabel}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
      {onDismiss && (
        <button
          className="btn-secondary"
          onClick={onDismiss}
          style={{ fontSize: 12, padding: "4px 10px", marginTop: 8 }}
        >
          ✓ Розрулила
        </button>
      )}
    </div>
  );
}

function ResourceLimitsBlock({
  limits,
  onChanged,
}: {
  limits: ResourceLimit[] | null;
  onChanged: () => void;
}) {
  const [label, setLabel] = useState("");
  const [pattern, setPattern] = useState("");
  const [capacity, setCapacity] = useState("2");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add() {
    if (!label.trim() || !pattern.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.addResourceLimit({
        label: label.trim(),
        pattern: pattern.trim(),
        capacity: Number(capacity) || 1,
      });
      setLabel("");
      setPattern("");
      setCapacity("2");
      onChanged();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!confirm("Видалити цей ліміт?")) return;
    try {
      await api.deleteResourceLimit(id);
      onChanged();
    } catch (e) {
      setError(String(e));
    }
  }

  async function toggle(l: ResourceLimit) {
    try {
      await api.patchResourceLimit(l.id, { isActive: !l.isActive });
      onChanged();
    } catch (e) {
      setError(String(e));
    }
  }

  return (
    <div className="card">
      <b>🪑 Ліміти ресурсів</b>
      <p className="hint" style={{ fontSize: 12, margin: "4px 0 10px" }}>
        Назва — для тебе; шаблон — слово, яке шукати в назві послуги (нечутливе
        до регістру). Якщо одночасно запланованих візитів &gt; ліміту — пушу
        алерт.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {limits === null ? (
          <div className="hint">Завантаження...</div>
        ) : limits.length === 0 ? (
          <p className="hint" style={{ margin: 0 }}>
            Ще нема жодного ліміту. Додай нижче, напр. «Педикюр → крісла → 2».
          </p>
        ) : (
          limits.map((l) => (
            <div
              key={l.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 8px",
                background: l.isActive
                  ? "transparent"
                  : "var(--tg-theme-secondary-bg-color, #f4f4f5)",
                borderRadius: 6,
                opacity: l.isActive ? 1 : 0.6,
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{l.label}</div>
                <div className="hint" style={{ fontSize: 11 }}>
                  «{l.pattern}» · {l.capacity} місць
                </div>
              </div>
              <button
                className="btn-secondary"
                onClick={() => toggle(l)}
                style={{ fontSize: 11, padding: "3px 8px" }}
              >
                {l.isActive ? "🟢 on" : "⚪ off"}
              </button>
              <button
                className="btn-secondary"
                onClick={() => remove(l.id)}
                style={{ fontSize: 11, padding: "3px 8px" }}
              >
                🗑
              </button>
            </div>
          ))
        )}
      </div>

      <div
        style={{
          marginTop: 12,
          paddingTop: 10,
          borderTop: "1px solid var(--tg-theme-hint-color, #ccc)",
        }}
      >
        <div className="hint" style={{ fontSize: 12, marginBottom: 6 }}>
          Додати ліміт:
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Назва (напр. Педикюрні крісла)"
            style={inputStyle}
          />
          <input
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            placeholder="Шаблон (напр. педикюр)"
            style={inputStyle}
          />
          <input
            value={capacity}
            onChange={(e) => setCapacity(e.target.value.replace(/[^0-9]/g, ""))}
            inputMode="numeric"
            placeholder="Скільки місць (напр. 2)"
            style={inputStyle}
          />
          <button
            className="btn"
            onClick={add}
            disabled={busy || !label.trim() || !pattern.trim()}
            style={{
              width: "100%",
              padding: "12px 16px",
              fontSize: 15,
              fontWeight: 600,
              marginTop: 4,
            }}
          >
            {busy ? "..." : "➕ Додати ліміт"}
          </button>
          {error && (
            <p className="hint" style={{ fontSize: 12, color: "#c0392b" }}>
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const inputStyle: CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 6,
  border: "1px solid var(--tg-theme-hint-color, #ccc)",
  background: "var(--tg-theme-bg-color, #fff)",
  color: "var(--tg-theme-text-color, #000)",
  fontSize: 14,
};

function ExpensesView({
  range,
  setRange,
}: {
  range: Range;
  setRange: (r: Range) => void;
}) {
  const [categories, setCategories] = useState<
    { id: string; name: string; kind: "fixed" | "variable" }[] | null
  >(null);
  const [expenses, setExpenses] = useState<any[] | null>(null);
  const [profit, setProfit] = useState<{
    services: number;
    tips: number;
    masterTakes: number;
    studioGross: number;
    expenses: number;
    studioNet: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [date, setDate] = useState(todayIso());
  const [categoryId, setCategoryId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  function load() {
    const { from, to } = rangeToDates(range);
    Promise.all([api.expenses(from, to), api.reports.profit(from, to)])
      .then(([ex, pf]) => {
        setExpenses(ex);
        setProfit(pf);
      })
      .catch((e) => setError(String(e)));
  }

  useEffect(() => {
    api.expenseCategories().then(setCategories).catch((e) => setError(String(e)));
  }, []);
  useEffect(load, [range]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!categoryId || !amount || saving) return;
    const amt = Number(amount.replace(",", "."));
    if (!(amt > 0)) {
      alert("Сума має бути більше 0");
      return;
    }
    setSaving(true);
    try {
      await api.addExpense({
        occurredOn: date,
        amount: amt,
        categoryId,
        note: note.trim() || undefined,
      });
      setAmount("");
      setNote("");
      load();
    } catch (e) {
      alert(String(e));
    } finally {
      setSaving(false);
    }
  }

  async function del(id: string, label: string) {
    if (!window.confirm(`Видалити витрату «${label}»?`)) return;
    try {
      await api.deleteExpense(id);
      load();
    } catch (e) {
      alert(String(e));
    }
  }

  if (error) {
    return (
      <div className="card">
        <p>Не вдалось завантажити витрати.</p>
        <p className="hint">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <PeriodPicker value={range} onChange={setRange} />

      {/* Net profit summary */}
      {profit && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>📊 Чистий прибуток студії · {RANGE_LABEL(range)}</h2>
          <div className="row" style={profitRow}>
            <span className="hint">Виручка (послуги + чай)</span>
            <span className="spacer" />
            <span style={{ fontWeight: 600 }}>
              {uahFmt(profit.services + profit.tips)} грн
            </span>
          </div>
          <div className="row" style={profitRow}>
            <span className="hint">Майстрам (40% + чайові)</span>
            <span className="spacer" />
            <span style={{ color: "#10b981", fontWeight: 600 }}>
              −{uahFmt(profit.masterTakes)}
            </span>
          </div>
          <div className="row" style={profitRow}>
            <span className="hint">Студія вало́м (60%)</span>
            <span className="spacer" />
            <span style={{ fontWeight: 600 }}>{uahFmt(profit.studioGross)} грн</span>
          </div>
          <div className="row" style={profitRow}>
            <span className="hint">Витрати студії</span>
            <span className="spacer" />
            <span style={{ color: "#e74c3c", fontWeight: 600 }}>
              −{uahFmt(profit.expenses)}
            </span>
          </div>
          <div
            className="row"
            style={{
              ...profitRow,
              borderBottom: 0,
              borderTop: "1px solid rgba(127,127,127,0.25)",
              marginTop: 6,
              paddingTop: 8,
            }}
          >
            <span style={{ fontWeight: 700 }}>Чистий прибуток</span>
            <span className="spacer" />
            <span
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: profit.studioNet >= 0 ? "#10b981" : "#e74c3c",
              }}
            >
              {profit.studioNet >= 0 ? "" : "−"}
              {uahFmt(Math.abs(profit.studioNet))} грн
            </span>
          </div>
        </div>
      )}

      {/* Add expense form */}
      <div className="card">
        <h2 style={{ marginTop: 0 }}>➕ Додати витрату</h2>
        <form onSubmit={add}>
          <div style={{ display: "flex", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={todayIso()}
              style={expField}
              required
            />
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              style={expField}
              required
            >
              <option value="">— Категорія —</option>
              <optgroup label="🗓️ Постійні">
                {categories
                  ?.filter((c) => c.kind === "fixed")
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {CATEGORY_ICONS[c.name] ?? "📦"} {c.name}
                    </option>
                  ))}
              </optgroup>
              <optgroup label="🌀 Змінні">
                {categories
                  ?.filter((c) => c.kind === "variable")
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {CATEGORY_ICONS[c.name] ?? "📦"} {c.name}
                    </option>
                  ))}
              </optgroup>
            </select>
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
            <input
              type="text"
              inputMode="decimal"
              placeholder="Сума, грн"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.,]/g, ""))}
              style={{ ...expField, flex: 1 }}
              required
            />
          </div>
          <textarea
            placeholder="Коментар (необовʼязково)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            style={{ ...expField, width: "100%", boxSizing: "border-box", marginBottom: 6, resize: "vertical" }}
          />
          <button
            type="submit"
            disabled={saving || !categoryId || !amount}
            style={{
              padding: "10px 16px",
              background: "var(--button)",
              color: "var(--button-text)",
              border: 0,
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              opacity: saving || !categoryId || !amount ? 0.5 : 1,
              width: "100%",
            }}
          >
            {saving ? "Зберігаю..." : "Додати"}
          </button>
        </form>
      </div>

      {/* Recent expenses */}
      <div className="card">
        <h2 style={{ marginTop: 0 }}>
          Останні витрати · {RANGE_LABEL(range)}
        </h2>
        {!expenses ? (
          <div className="hint">Завантаження...</div>
        ) : expenses.length === 0 ? (
          <p className="hint" style={{ margin: 0 }}>За період витрат нема.</p>
        ) : (
          expenses.map((e: any) => {
            const d = new Date(e.occurredOn);
            const dateStr = `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
            const icon = CATEGORY_ICONS[e.categoryName] ?? "📦";
            return (
              <div key={e.id} style={expRow}>
                <div className="row">
                  <span style={{ fontWeight: 600, minWidth: 50 }}>{dateStr}</span>
                  <span style={{ flex: 1 }}>
                    {icon} {e.categoryName}
                    {e.isVirtual && (
                      <span
                        className="hint"
                        style={{ fontSize: 10, marginLeft: 6 }}
                      >
                        авто
                      </span>
                    )}
                  </span>
                  <span style={{ fontWeight: 700, color: "#e74c3c" }}>
                    −{uahFmt(e.amount)} грн
                  </span>
                  {!e.isVirtual && (
                    <button
                      onClick={() => del(e.id, `${e.categoryName} · ${e.amount}`)}
                      style={expDelBtn}
                      title="Видалити"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {e.note && (
                  <div className="hint" style={{ fontSize: 12, marginTop: 2 }}>
                    {e.note}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

const profitRow: CSSProperties = {
  padding: "4px 0",
  borderBottom: "1px solid rgba(127,127,127,0.1)",
  fontSize: 14,
};

const expRow: CSSProperties = {
  padding: 8,
  background: "var(--bg)",
  border: "1px solid rgba(127,127,127,0.2)",
  borderRadius: 8,
  marginBottom: 6,
};

const expField: CSSProperties = {
  padding: "8px 10px",
  border: "1px solid rgba(127,127,127,0.3)",
  borderRadius: 6,
  background: "var(--bg)",
  color: "var(--text)",
  fontSize: 14,
  fontFamily: "inherit",
};

const expDelBtn: CSSProperties = {
  background: "transparent",
  color: "#e74c3c",
  border: 0,
  fontSize: 16,
  cursor: "pointer",
  padding: "0 4px",
  marginLeft: 6,
};

function MasterRow({
  m,
  source,
  range,
  onPaid,
  onOpen,
}: {
  m: any;
  source: Source;
  range: Range;
  onPaid: () => void;
  onOpen?: () => void;
}) {
  const [paying, setPaying] = useState(false);
  const revenue = source === "cash" ? m.cashRevenue : m.bumpixRevenue;
  const masterTakes =
    source === "cash"
      ? m.masterTakes
      : Math.round(m.bumpixRevenue * MASTER_SHARE);
  const studioTakes =
    source === "cash"
      ? m.studioTakes
      : Math.round(m.bumpixRevenue * STUDIO_SHARE);

  async function payOut() {
    if (paying) return;
    const ok = window.confirm(
      `Виплатити ${uahFmt(m.pendingPayout)} грн для ${m.name}?\n\nЦе закриє всі касові записи за період і їх не можна буде ще раз виплатити.`,
    );
    if (!ok) return;
    setPaying(true);
    try {
      const { from, to } = rangeToDates(range);
      await api.payoutMaster(m.masterId, from, to);
      onPaid();
    } catch (e) {
      alert(String(e));
    } finally {
      setPaying(false);
    }
  }

  return (
    <div style={masterRow}>
      <div className="row">
        {onOpen ? (
          <button
            onClick={onOpen}
            style={{
              background: "transparent",
              border: 0,
              padding: 0,
              fontWeight: 700,
              color: "var(--link)",
              cursor: "pointer",
              fontSize: "inherit",
            }}
          >
            {m.name} ›
          </button>
        ) : (
          <strong>{m.name}</strong>
        )}
        <span className="spacer" />
        <span className="hint">{m.visits} віз.</span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: source === "cash" ? "repeat(4, 1fr)" : "repeat(3, 1fr)",
          gap: 4,
          marginTop: 6,
          fontSize: 12,
        }}
      >
        <div>
          <div className="hint">Виручка</div>
          <div style={{ fontWeight: 600 }}>{uahFmt(revenue)}</div>
        </div>
        <div>
          <div className="hint">Майстру</div>
          <div style={{ fontWeight: 600, color: "#10b981" }}>{uahFmt(masterTakes)}</div>
        </div>
        {source === "cash" && (
          <div>
            <div className="hint">Виплачено</div>
            <div style={{ fontWeight: 600, color: "#0ea5e9" }}>{uahFmt(m.paidOut)}</div>
          </div>
        )}
        <div>
          <div className="hint">Студії</div>
          <div style={{ fontWeight: 600, color: "#7c3aed" }}>{uahFmt(studioTakes)}</div>
        </div>
      </div>

      {source === "cash" && masterTakes > 0 && (
        <div
          style={{
            marginTop: 8,
            paddingTop: 8,
            borderTop: "1px dashed rgba(127,127,127,0.25)",
          }}
        >
          {m.pendingPayout > 0 ? (
            <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
              <div style={{ flex: 1, fontSize: 13, minWidth: 0 }}>
                💵 До виплати: <b>{uahFmt(m.pendingPayout)}</b> грн
                {m.pendingTips > 0 && m.pendingFromPrices > 0 && (
                  <span className="hint">
                    {" "}({uahFmt(m.pendingFromPrices)} з послуг + {uahFmt(m.pendingTips)} 💸)
                  </span>
                )}
                {m.paidOut > 0 && (
                  <span className="hint"> · вже виплачено {uahFmt(m.paidOut)}</span>
                )}
              </div>
              <button onClick={payOut} disabled={paying} style={payBtn}>
                {paying
                  ? "..."
                  : m.pendingTips > 0 && m.pendingFromPrices > 0
                    ? `Виплатити ${uahFmt(m.pendingFromPrices)} + ${uahFmt(m.pendingTips)} 💸`
                    : "Виплатити"}
              </button>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "#10b981" }}>
              ✅ Виплачено все за період ({uahFmt(m.paidOut)} грн)
            </div>
          )}
        </div>
      )}

      {source === "cash" && m.cashTips > 0 && (
        <div className="hint" style={{ marginTop: 4, fontSize: 11 }}>
          💸 чайові: {uahFmt(m.cashTips)} (в «Майстру»)
        </div>
      )}
      {source === "cash" &&
        m.bumpixRevenue > 0 &&
        Math.abs(m.bumpixRevenue - m.cashRevenue) > 1 && (
          <div className="hint" style={{ marginTop: 2, fontSize: 11 }}>
            📋 Bumpix казав: {uahFmt(m.bumpixRevenue)} (
            {m.bumpixRevenue > m.cashRevenue ? "+" : ""}
            {uahFmt(m.bumpixRevenue - m.cashRevenue)} різниця)
          </div>
        )}
    </div>
  );
}

function TopClientsCard({
  items,
  range,
  onOpen,
}: {
  items: TopClient[] | null;
  range: Range;
  onOpen: (id: string) => void;
}) {
  if (!items) return null;
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>
        🏆 Топ клієнтів по грошах · {RANGE_LABEL(range)}
      </h2>
      <p className="hint" style={{ marginBottom: 8 }}>
        Хто найбільше ходить і платить (ціни з Bumpix). Тицяй — відкриється профіль.
      </p>
      {items.length === 0 ? (
        <p className="hint" style={{ margin: 0 }}>
          За цей період даних нема.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map((c, i) => {
            const name = `${c.firstName} ${c.lastName ?? ""}`.trim();
            const avg = c.visits > 0 ? c.spent / c.visits : 0;
            const rank =
              i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`;
            return (
              <button
                key={c.clientId}
                onClick={() => onOpen(c.clientId)}
                style={topClientRow}
              >
                <div className="row">
                  <span
                    style={{
                      minWidth: 30,
                      fontWeight: 700,
                      color: "var(--hint)",
                    }}
                  >
                    {rank}
                  </span>
                  <span style={{ fontWeight: 600, flex: 1 }}>{name}</span>
                  <span style={{ fontWeight: 700, color: "#10b981" }}>
                    {uahFull(c.spent)} грн
                  </span>
                </div>
                <div
                  className="hint"
                  style={{ fontSize: 12, marginTop: 2, marginLeft: 30 }}
                >
                  {c.visits} віз. · сер. {uahFull(avg)} грн
                  {c.tips > 0 ? ` · 💸 ${uahFull(c.tips)} чай` : ""}
                  <span style={{ float: "right" }}>›</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const topClientRow: CSSProperties = {
  display: "block",
  width: "100%",
  textAlign: "left",
  background: "var(--bg)",
  color: "var(--text)",
  border: "1px solid rgba(127,127,127,0.2)",
  borderRadius: 8,
  padding: 10,
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: 14,
};

const gridStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: 8,
  marginBottom: 12,
};

const masterRow: CSSProperties = {
  background: "var(--bg)",
  border: "1px solid rgba(127,127,127,0.2)",
  borderRadius: 8,
  padding: 10,
};

function OrphanedCashBanner({
  range,
  byMaster,
  onChanged,
}: {
  range: Range;
  byMaster: any[] | null;
  onChanged: () => void;
}) {
  const [items, setItems] = useState<any[] | null>(null);
  const [open, setOpen] = useState(false);

  async function load() {
    const { from, to } = rangeToDates(range);
    const data = await api.listCash({ unassigned: true, from, to });
    setItems(data as any[]);
  }
  useEffect(() => {
    void load();
  }, [range]);

  if (!items || items.length === 0) return null;

  const total = items.reduce((s, e) => s + Number(e.amount) + Number(e.tip), 0);

  async function del(id: string) {
    if (!window.confirm("Видалити цей запис? Дію не можна скасувати.")) return;
    try {
      await api.deleteCash(id);
      await load();
      onChanged();
    } catch (e) {
      alert(String(e));
    }
  }

  async function reassign(id: string, masterId: string) {
    try {
      await api.reassignCash(id, masterId);
      await load();
      onChanged();
    } catch (e) {
      alert(String(e));
    }
  }

  const masters = (byMaster ?? []).filter((m) => m.name);

  return (
    <div
      className="card"
      style={{ background: "rgba(255,193,7,0.15)", borderRadius: 12 }}
    >
      <button onClick={() => setOpen(!open)} style={orphanHeader}>
        {open ? "▾" : "▸"} 🤖 Записи без майстра: {items.length} ({uahFmt(total)} грн)
      </button>
      {open && (
        <>
          <p className="hint" style={{ marginTop: 6, marginBottom: 8 }}>
            Майстер пересилала з прихованого профілю → бот не зміг розпізнати
            кого це. Можеш призначити майстра або видалити.
          </p>
          {items.map((e) => (
            <div key={e.id} style={orphanRow}>
              <div className="row" style={{ marginBottom: 4 }}>
                <span style={{ fontWeight: 600 }}>
                  {new Date(e.occurredOn).toLocaleDateString("uk-UA")}
                </span>
                <span className="spacer" />
                <span style={{ fontWeight: 600 }}>
                  {uahFmt(Number(e.amount))} {e.tip > 0 ? `+ ${uahFmt(Number(e.tip))} 💸` : ""}
                </span>
              </div>
              <div className="hint" style={{ fontSize: 12, marginBottom: 6 }}>
                {e.service ?? "?"}
                {e.discountPercent ? ` · −${e.discountPercent}%` : ""}
                {e.isWarranty ? " · гарантія" : ""}
                {e.rawMessage ? ` · "${e.rawMessage}"` : ""}
              </div>
              <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                <select
                  defaultValue=""
                  onChange={(ev) => {
                    if (ev.target.value) {
                      void reassign(e.id, ev.target.value);
                      ev.target.value = "";
                    }
                  }}
                  style={selectInline}
                >
                  <option value="">Призначити майстра…</option>
                  {masters.map((m: any) => (
                    <option key={m.masterId} value={m.masterId}>
                      {m.name}
                    </option>
                  ))}
                </select>
                <button onClick={() => del(e.id)} style={delBtn}>
                  ✕ Видалити
                </button>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

const orphanHeader: CSSProperties = {
  width: "100%",
  textAlign: "left",
  background: "transparent",
  color: "var(--text)",
  border: 0,
  fontWeight: 600,
  fontSize: 14,
  cursor: "pointer",
  padding: 0,
};

const orphanRow: CSSProperties = {
  background: "var(--bg)",
  border: "1px solid rgba(127,127,127,0.2)",
  borderRadius: 8,
  padding: 8,
  marginTop: 6,
};

const selectInline: CSSProperties = {
  padding: "6px 8px",
  border: "1px solid rgba(127,127,127,0.3)",
  borderRadius: 6,
  background: "var(--bg)",
  color: "var(--text)",
  fontSize: 12,
  flex: 1,
  minWidth: 140,
};

const delBtn: CSSProperties = {
  padding: "6px 10px",
  background: "transparent",
  color: "#e74c3c",
  border: "1px solid #e74c3c",
  borderRadius: 6,
  fontSize: 12,
  cursor: "pointer",
};

const payBtn: CSSProperties = {
  padding: "8px 14px",
  background: "#10b981",
  color: "white",
  border: 0,
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

const payoutLine: CSSProperties = {
  background: "var(--bg)",
  border: "1px solid rgba(127,127,127,0.2)",
  borderRadius: 8,
  padding: 8,
  marginBottom: 6,
};

const preStyle: CSSProperties = {
  background: "var(--secondary-bg)",
  padding: 8,
  borderRadius: 6,
  fontSize: 13,
  margin: "4px 0 0",
  whiteSpace: "pre-wrap",
};
